import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Download, Check, Mail, Clock, AlertTriangle, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuyEbookSuccess() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('No download token in the URL.');
      setLoading(false);
      return;
    }
    // Mark the order complete (idempotent), then fetch its status.
    axios
      .post(`${API}/ebook/order/${token}/complete`)
      .catch(() => {}) // non-fatal; order may already be completed
      .then(() => axios.get(`${API}/ebook/order/${token}`))
      .then((r) => setOrder(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load your order.'))
      .finally(() => setLoading(false));
  }, [token]);

  const downloadHref = order?.download_url || `${API}/ebook/download/${token}`;

  return (
    <div>
      <SEO title="Thank you — Your Ebook is Ready" path="/single-handed/buy/success" />

      <section className="min-h-[70vh] bg-gradient-to-b from-slate-50 to-white py-16">
        <div className="max-w-2xl mx-auto px-4">
          {loading ? (
            <div className="text-center text-slate-500 py-20">Loading your order…</div>
          ) : error ? (
            <ErrorState message={error} />
          ) : order?.expired ? (
            <ExpiredState />
          ) : order?.downloads_remaining === 0 ? (
            <LimitReachedState />
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 text-amber-600 mb-5">
                  <Check className="h-8 w-8" strokeWidth={3} />
                </div>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">
                  Thank you for your purchase!
                </h1>
                <p className="text-slate-600 mt-3">
                  Your copy of <strong>{order.title}</strong> by {order.author} is ready to download.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 md:p-8">
                <div className="grid sm:grid-cols-2 gap-4 pb-5 border-b border-slate-100 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Order #</div>
                    <div className="font-mono text-[#0b2545] mt-1">{order.order_number}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Amount paid</div>
                    <div className="text-[#0b2545] mt-1">${order.amount_usd} USD</div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                    <div className="text-[#0b2545] mt-1">{order.buyer_email}</div>
                  </div>
                </div>

                <div className="my-6 text-center">
                  <a
                    href={downloadHref}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-bold text-lg shadow-md hover:shadow-lg transition-all"
                  >
                    <Download className="h-5 w-5" /> Download your ebook (EPUB)
                  </a>
                  <div className="mt-3 text-xs text-slate-500">
                    Downloads remaining: <strong>{order.downloads_remaining}</strong> of 5
                    {order.expires_at && (
                      <>
                        {' '}· Link expires <strong>{new Date(order.expires_at).toLocaleString()}</strong>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-6 p-4 bg-slate-50 rounded-md border border-slate-200 flex items-start gap-3 text-sm text-slate-700">
                  <Mail className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    A confirmation email with this secure download link has been sent to <strong>{order.buyer_email}</strong>.
                    If you don’t see it in your inbox, please check your spam folder.
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Button asChild variant="outline" className="border-[#0b2545] text-[#0b2545] hover:bg-[#0b2545] hover:text-white">
                  <Link to="/single-handed"><BookOpen className="mr-2 h-4 w-4" /> Back to Single Handed</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="text-center bg-white rounded-lg border border-slate-200 p-8">
      <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
      <h2 className="font-serif text-2xl font-bold text-[#0b2545] mb-2">We couldn’t find your order</h2>
      <p className="text-slate-600 mb-6">{message}</p>
      <Button asChild><Link to="/single-handed/buy">Start a new order</Link></Button>
    </div>
  );
}

function ExpiredState() {
  return (
    <div className="text-center bg-white rounded-lg border border-slate-200 p-8">
      <Clock className="h-10 w-10 text-amber-500 mx-auto mb-4" />
      <h2 className="font-serif text-2xl font-bold text-[#0b2545] mb-2">Download link expired</h2>
      <p className="text-slate-600 mb-6">
        For security, download links expire after 48 hours. Please contact us if you need help accessing your ebook.
      </p>
      <Button asChild><Link to="/contact">Contact support</Link></Button>
    </div>
  );
}

function LimitReachedState() {
  return (
    <div className="text-center bg-white rounded-lg border border-slate-200 p-8">
      <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
      <h2 className="font-serif text-2xl font-bold text-[#0b2545] mb-2">Download limit reached</h2>
      <p className="text-slate-600 mb-6">
        You’ve reached the maximum number of downloads for this order. Please contact us if you need another copy.
      </p>
      <Button asChild><Link to="/contact">Contact support</Link></Button>
    </div>
  );
}
